import React, { useState } from 'react'
import {
  CancelOutlined,
  GetApp as GetAppIcon,
  Visibility,
  HighlightOff as HighlightOffIcon,
} from '@mui/icons-material'
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  CircularProgress,
  Box,
  DialogContent,
  Pagination,
} from '@mui/material'
import axios from 'axios'
import { useAuthContext } from '../../hooks/useAuthContext'
import { fileServices } from '../../services/fileServices'
import { downloadBlob } from '../../utility/fileUtil'
import Loader from '../core-ui/Loader'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

const docHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
}

const docTitleStyle = {
  padding: '0 0 15px 26px',
  color: '#0000EE',
  fontSize: 14,
}

const docIconStyle = {
  padding: '0 26px 15px 0px',
  color: '#0000EE',
  size: 'small',
}

const AutoGeneratedDocumentList = ({
  title,
  isDocOpen,
  setIsDocOpen,
  setTimelineOpen = () => {},
  docData = [],
  selectedNode,
}) => {
  const { user } = useAuthContext()
  const [loading, setLoading] = useState(new Set())
  const [previewLoading, setPreviewLoading] = useState(false)
  const [images, setImages] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [previewLoaded, setPreviewLoaded] = useState(false)

  const handlePageChange = (_, value) => {
    setCurrentPage(value)
  }

  const itemsPerPage = 1
  const totalPages = Math.ceil(images.length / itemsPerPage)

  const downloadFile = async (fileName, index) => {
    if (loading.has(index)) {
      return
    }

    setLoading((prev) => new Set([...prev, index]))

    try {
      const result = await fileServices.downloadFile(user, fileName, 'documents_for_template')
      if (result?.data) {
        downloadBlob(result?.data, fileName)
      }
    } catch (error) {
      console.error('Error downloading file:', error)
    } finally {
      setLoading((prev) => {
        const updated = new Set(prev)
        updated.delete(index)
        return updated
      })
      setPreviewLoaded(true)
    }
  }

  const getPreviewData = async (key) => {
    try {
      setPreviewLoading(true)
      setIsDocOpen(false)

      const result = (
        await axios.get(
          `${NODE_BASE_URL}/data-entry/file/base64-images/${key}?folder=documents_for_template`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        )
      ).data

      setImages(result)
      setCurrentPage(result.length)
    } catch (e) {
      setImages([])
    }
  }

  return (
    <>
      <Dialog
        open={isDocOpen}
        style={{ width: '-webkit-fill-available' }}
        onClose={() => {
          setIsDocOpen(false)
          setTimelineOpen(true)
        }}
        fullWidth={true}
      >
        <div style={docHeaderStyle}>
          <DialogTitle id="alert-dialog-title">
            <Typography variant="h4" style={{ fontSize: 14, fontWeight: 'bold' }}>
              {title}
            </Typography>
            <Typography variant="p" style={{ fontSize: 14, fontWeight: 'normal' }}>
              {selectedNode.activity_template_name} {`(v${selectedNode.version})`}
            </Typography>
          </DialogTitle>
          <div style={{ display: 'inline-block', margin: '20px 28px 0px 0px' }}>
            <IconButton
              align="right"
              onClick={() => {
                setIsDocOpen(false)
                setTimelineOpen(true)
              }}
              size="large"
            >
              <HighlightOffIcon />
            </IconButton>
          </div>
        </div>

        {docData &&
          docData.map((file, i) => (
            <div
              key={i}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span style={docTitleStyle}>{file.document_template_name}</span>
              <span style={docIconStyle}>
                <IconButton
                  onClick={() => {
                    getPreviewData(file.document_key)
                    setIsDocOpen(true)
                  }}
                >
                  {images == null ? (
                    <CircularProgress color="primary" size={'25px'} />
                  ) : (
                    <Visibility color="info" size="small" />
                  )}
                </IconButton>

                <IconButton
                  size="small"
                  download
                  onClick={() => downloadFile(file.document_key, i)}
                >
                  {loading.has(i) ? (
                    <CircularProgress color="info" size={'25px'} />
                  ) : (
                    <GetAppIcon color="info" size="small" />
                  )}
                </IconButton>
              </span>
            </div>
          ))}
      </Dialog>
      <Dialog
        open={previewLoading}
        onClose={() => {
          setIsDocOpen(true)
          setPreviewLoading(false)
        }}
        maxWidth="xxl"
      >
        <Box sx={{ width: '100%' }}>
          {previewLoading && (
            <Box sx={{ width: '100%', position: 'sticky', top: 0 }}>
              <DialogContent>
                {images.length === 0 ? (
                  previewLoaded ? (
                    <div>Preview not available. Click the "Download" icon to download file </div>
                  ) : (
                    <div>
                      <Loader message={'Loading Preview'} />
                    </div>
                  )
                ) : (
                  <>
                    <div style={{ position: 'relative' }}>
                      {images && (
                        <img
                          style={{ width: '100%', height: 'auto' }}
                          src={images[currentPage - 1]}
                          alt="File Viewer"
                        />
                      )}
                      <div style={{ position: 'absolute', top: 0, right: 0 }}>
                        <Button
                          color="info"
                          size="small"
                          onClick={() => {
                            setIsDocOpen(true)
                            setPreviewLoading(false)
                          }}
                        >
                          <CancelOutlined size="small" />
                        </Button>
                      </div>
                    </div>
                    {images && (
                      <Box
                        sx={{
                          maxWidth: '800px',
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '10px',
                          backgroundColor: 'white',
                          position: 'fixed',
                          bottom: '5%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          variant="outlined"
                          shape="rounded"
                        />
                      </Box>
                    )}
                  </>
                )}
              </DialogContent>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  )
}

export default AutoGeneratedDocumentList
